// src/components/shared/forms/EntityServicesForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  BarChart as AnalyticsIcon,
} from '@mui/icons-material';
import { Card } from '../../../design-system/components/core';
import { PLATFORM_OPTIONS } from '../../features/content/form-utils/contentFormData';
import { PlatformChip } from '../../ui';
import { FormSection } from '../../../design-system/components/forms';
import { TextField } from '../../../design-system/components/forms';

// Platform configuration with colors for proper highlighting
const PLATFORM_CONFIG = {
  facebook: { color: '#1877F2' },
  instagram: { color: '#E1306C' },
  linkedin: { color: '#0077B5' },
  twitter: { color: '#1DA1F2' },
  youtube: { color: '#FF0000' },
  tiktok: { color: '#000000' },
  snapchat: { color: '#FFFC00' }
};

// Import custom components
const platforms = PLATFORM_OPTIONS;

/**
 * EntityServicesForm - A reusable form component for configuring services
 * across different entity types (clients, brands, campaigns, etc.)
 * 
 * @param {Object} props Component props
 * @param {Object} props.data The entity data containing serviceConfig
 * @param {Object} props.defaultConfig Default configuration for new entities
 * @param {Function} props.onChange Legacy change handler
 * @param {Function} props.onNestedChange Nested field change handler
 * @param {Function} props.setFieldValue Formik-style field setter
 * @param {Object} props.errors Validation errors
 * @param {Object} props.touched Touched fields
 * @param {String} props.configField Field name for storing service config (default: 'serviceConfig')
 * @param {Array} props.availableServices Custom list of available services
 * @param {String} props.title Section title (default: 'Service Configuration')
 * @param {Boolean} props.showPlatforms Whether to show platforms section
 * @param {Boolean} props.showServices Whether to show services section
 * @param {Boolean} props.showPostingFrequency Whether to show posting frequency section
 * @param {Boolean} props.showReportingFrequency Whether to show reporting frequency section
 */
const EntityServicesForm = ({ 
  data = {},
  defaultConfig = {
    platforms: {
      facebook: false,
      instagram: false,
      linkedin: false,
      twitter: false,
      tiktok: false,
      youtube: false,
      snapchat: false
    },
    services: {
      socialMediaManagement: false,
      contentCreation: false,
      campaignManagement: false,
      influencerMarketing: false,
      analyticsReporting: false,
      communityManagement: false,
      paidAdvertising: false,
      brandingService: false,
      packagingService: false
    },
    postingFrequency: 'alternate_days',
    customFrequency: 2,
    reportingFrequency: 'monthly'
  },
  onChange, 
  onNestedChange,
  setFieldValue,
  errors = {},
  touched = {},
  configField = 'serviceConfig',
  availableServices = [
    { id: 'socialMediaManagement', label: 'Social Media Management' },
    { id: 'contentCreation', label: 'Content Creation' },
    { id: 'campaignManagement', label: 'Campaign Management' },
    { id: 'influencerMarketing', label: 'Influencer Marketing' },
    { id: 'analyticsReporting', label: 'Analytics & Reporting' },
    { id: 'communityManagement', label: 'Community Management' },
    { id: 'paidAdvertising', label: 'Paid Advertising' },
    { id: 'brandingService', label: 'Branding Service' },
    { id: 'packagingService', label: 'Packaging Service' }
  ],
  title = 'Service Configuration',
  showPlatforms = true,
  showServices = true,
  showPostingFrequency = true,
  showReportingFrequency = true,
}) => {
  const theme = useTheme();
  
  // Get service config from entity data or initialize with defaults
  const serviceConfig = data[configField] || defaultConfig;
  
  // Handle platform toggle with backward compatibility
  const handlePlatformChange = (platform) => {
    const updatedPlatforms = {
      ...serviceConfig.platforms,
      [platform]: !serviceConfig.platforms[platform]
    };
    
    // Support legacy nested change handler
    if (onNestedChange) {
      onNestedChange(configField, 'platforms', updatedPlatforms);
    } 
    // Support new formik-style change handler
    else if (setFieldValue) {
      setFieldValue(`${configField}.platforms`, updatedPlatforms);
    }
    // Support direct onChange handler (using name, value pattern)
    else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${configField}.platforms`, updatedPlatforms);
    }
  };
  
  // Handle service toggle with backward compatibility
  const handleServiceChange = (service) => {
    const updatedServices = {
      ...serviceConfig.services,
      [service]: !serviceConfig.services[service]
    };
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange(configField, 'services', updatedServices);
    } else if (setFieldValue) {
      setFieldValue(`${configField}.services`, updatedServices);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${configField}.services`, updatedServices);
    }
  };
  
  // Handle posting frequency change with backward compatibility
  const handlePostingFrequencyChange = (e) => {
    const value = e.target.value;
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange(configField, 'postingFrequency', value);
    } else if (setFieldValue) {
      setFieldValue(`${configField}.postingFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${configField}.postingFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
  };
  
  // Handle custom frequency change with backward compatibility
  const handleCustomFrequencyChange = (e) => {
    const value = parseInt(e.target.value, 10);
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange(configField, 'customFrequency', value);
    } else if (setFieldValue) {
      setFieldValue(`${configField}.customFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${configField}.customFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
  };
  
  // Handle reporting frequency change with backward compatibility
  const handleReportingFrequencyChange = (e) => {
    const value = e.target.value;
    
    // Support multiple API patterns
    if (onNestedChange) {
      onNestedChange(configField, 'reportingFrequency', value);
    } else if (setFieldValue) {
      setFieldValue(`${configField}.reportingFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length === 2) {
      onChange(`${configField}.reportingFrequency`, value);
    } else if (onChange && typeof onChange === 'function' && onChange.length !== 2) {
      onChange(e);
    }
  };
  
  return (
    <Grid container spacing={4}>
      {/* Service Configuration */}
      <Grid item xs={12}>
        <FormSection
          icon={<SettingsIcon />}
          title={title}
        >
          <Card sx={{ mb: 3 }}>
            {showPlatforms && (
              <>
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Social Media Platforms
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    {platforms.map((item) => {
                      const platform = item.value;
                      return (
                        <Box
                          key={platform}
                          sx={{
                            display: 'inline-block',
                            mr: 2,
                            mb: 1,
                            position: 'relative',
                            '&::after': serviceConfig.platforms[platform] ? {
                              content: '""',
                              position: 'absolute',
                              bottom: -4,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: PLATFORM_CONFIG[platform]?.color || '#1877F2',
                              boxShadow: (theme) => `0 0 8px ${PLATFORM_CONFIG[platform]?.color || '#1877F2'}`
                            } : {}
                          }}
                        >
                          <Checkbox
                            checked={serviceConfig.platforms[platform]}
                            onChange={() => handlePlatformChange(platform)}
                            icon={<PlatformChip platform={platform} />}
                            checkedIcon={<PlatformChip platform={platform} variant="filled" />}
                            sx={{
                              padding: 0.5,
                              '&.Mui-checked': {
                                color: PLATFORM_CONFIG[platform]?.color || '#1877F2',
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                {(showServices || showPostingFrequency || showReportingFrequency) && <Divider />}
              </>
            )}
            
            {showServices && (
              <>
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Services
                  </Typography>
                  <Grid container spacing={2}>
                    {availableServices.map((service) => (
                      <Grid item xs={12} sm={6} md={4} key={service.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={serviceConfig.services[service.id]}
                              onChange={() => handleServiceChange(service.id)}
                            />
                          }
                          label={service.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                {(showPostingFrequency || showReportingFrequency) && <Divider />}
              </>
            )}
            
            {(showPostingFrequency || showReportingFrequency) && (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {showPostingFrequency && (
                    <Grid item xs={12} md={showReportingFrequency ? 6 : 12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ScheduleIcon sx={{ mr: 1 }} />
                            Posting Frequency
                          </Box>
                        </FormLabel>
                        <RadioGroup
                          name="postingFrequency"
                          value={serviceConfig.postingFrequency}
                          onChange={handlePostingFrequencyChange}
                        >
                          <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                          <FormControlLabel value="alternate_days" control={<Radio />} label="Alternate Days" />
                          <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                          <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                        </RadioGroup>
                        
                        {serviceConfig.postingFrequency === 'custom' && (
                          <TextField
                            label="Days Between Posts"
                            type="number"
                            value={serviceConfig.customFrequency}
                            onChange={handleCustomFrequencyChange}
                            InputProps={{
                              inputProps: { min: 1, max: 30 },
                              endAdornment: <InputAdornment position="end">days</InputAdornment>,
                            }}
                            sx={{ mt: 2, width: 200 }}
                          />
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  
                  {showReportingFrequency && (
                    <Grid item xs={12} md={showPostingFrequency ? 6 : 12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AnalyticsIcon sx={{ mr: 1 }} />
                            Reporting Frequency
                          </Box>
                        </FormLabel>
                        <RadioGroup
                          name="reportingFrequency"
                          value={serviceConfig.reportingFrequency}
                          onChange={handleReportingFrequencyChange}
                        >
                          <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                          <FormControlLabel value="biweekly" control={<Radio />} label="Bi-weekly" />
                          <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                          <FormControlLabel value="quarterly" control={<Radio />} label="Quarterly" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Card>
        </FormSection>
      </Grid>
    </Grid>
  );
};

export default EntityServicesForm;